import { ShowHeader } from "@/components";
import { useGetUser } from "@/queries";
import { useUIStore } from "@/stores";
import { UserRole, UserStatus } from "@/types";
import {
  calculateAge,
  formatDate,
  formatInstProfile, formatUserName,
  formatUserRoleToName,
  ImgSrc
} from "@/utils";
import { Instagram } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { purple } from "@mui/material/colors";
import { FC } from "react";
import { Loading, useNotify, useRedirect } from "react-admin";
import { PhotoProvider, PhotoView } from "react-photo-view";
import { useParams } from "react-router-dom";
import { ApproveUser, RejectUser } from "../../modals";
import { useReissuePaymentLink } from "../../queries";

const VettingShow: FC = () => {
  const { id } = useParams();

  const notify = useNotify();
  const redirect = useRedirect();
  const { openModal } = useUIStore();

  const { user, isUserLoading } = useGetUser(id ?? "");
  const { reissuePaymentLink, isReissueLoading } = useReissuePaymentLink();

  const isApprovedAlpha =
    user?.role === UserRole.ALPHA &&
    (user?.status === UserStatus.NOT_FINISHED_PROFILE ||
      user?.status === UserStatus.ACTIVE);

  if (isUserLoading) {
    return <Loading />;
  }

  if (!user) {
    redirect("list");
    notify("Vetting user not found", { type: "error" });
    return;
  }

  return (
    <Stack direction={"column"} p={2}>
      <ShowHeader
        title="Vetting"
        rightAddon={
          <>
            {user.status === UserStatus.PENDING && (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() =>
                    openModal(RejectUser, {
                      title: "Reject user",
                      userId: user.id,
                    })
                  }
                >
                  Reject
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    openModal(ApproveUser, {
                      title: "Approve user",
                      userId: user.id,
                      userRole: user.role,
                    })
                  }
                >
                  Approve
                </Button>
              </Stack>
            )}
            {isApprovedAlpha && (
              <Button
                variant="contained"
                color="info"
                loading={isReissueLoading}
                disabled={isReissueLoading}
                onClick={() => reissuePaymentLink({ userId: user.id })}
              >
                Re-issue Payment Link
              </Button>
            )}
            {user.status === UserStatus.REJECTED && (
              <Stack direction={"row"} spacing={2}>
                <Chip variant="filled" color="error" label="Rejected" />
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={() =>
                    openModal(ApproveUser, {
                      title: "Approve user",
                      userId: user.id,
                      userRole: user.role,
                    })
                  }
                >
                  Approve
                </Button>
              </Stack>
            )}
          </>
        }
      />

      <Grid spacing={4} item xs={12}>
        <Box display="flex" gap={2}>
          <Avatar
            src={ImgSrc(user.profileImage?.path ?? "")}
            alt="Kristin Watson"
            sx={{ width: 56, height: 56 }}
          />
          <Stack direction={"column"}>
            <Stack direction={"row"} alignItems={"center"} gap={2}>
              <Typography variant="h6" fontWeight="bold">
                {formatUserName(user?.firstName, user?.lastName, user.status)}
              </Typography>
            </Stack>
            {user.socialLinks?.instagram && (
              <Stack direction={"row"} alignItems={"center"} columnGap={0.5}>
                <Instagram fontSize="small" sx={{ color: purple[200] }} />
                <Link
                  sx={{ color: purple[200], textDecoration: "none" }}
                  href={user.socialLinks.instagram}
                  target={"_blank"}
                >
                  {formatInstProfile(user.socialLinks.instagram)}
                </Link>
              </Stack>
            )}
          </Stack>
        </Box>

        <Divider sx={{ margin: "16px 0" }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Role
            </Typography>
            <Typography variant="subtitle2">
              {formatUserRoleToName(user.role)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Date of Application
            </Typography>
            <Typography variant="subtitle2">
              {formatDate(user.createdAt)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Referrer
            </Typography>
            <Typography variant="subtitle2">
              {user.referrer
                ? `${user.referrer.firstName} ${user.referrer.lastName}`
                : "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Email Address
            </Typography>
            <Typography variant="subtitle2">{user.email}</Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Phone Number
            </Typography>
            <Typography variant="subtitle2">
              {user.phoneNumber || "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Origin
            </Typography>
            <Typography variant="subtitle2">
              {user.locationOrigin.label}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Living
            </Typography>
            <Typography variant="subtitle2">
              {user.locationResidence.label}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Age
            </Typography>
            <Typography variant="subtitle2">
              {calculateAge(user.dateOfBirth)}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Place of Work
            </Typography>
            <Typography variant="subtitle2">
              {user.locationWorkplace || "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Occupation
            </Typography>
            <Typography variant="subtitle2">
              {user.occupation || "—"}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Typography variant="body2" color="gray">
              Affiliate
            </Typography>
            <Typography variant="subtitle2">
              {user.affiliate ? user.affiliate.fullName : "—"}
            </Typography>
          </Grid>
        </Grid>

        {!!user.accomplishments?.length && (
          <Stack mt={1} spacing={1}>
            <Typography variant="body2" color="gray">
              Accomplishments
            </Typography>
            <Stack>
              {user.accomplishments.map(({ description, link }, index) => (
                <Typography>
                  {index + 1}.&nbsp;&nbsp;
                  <Link key={index} href={link} target="_blank">
                    {description}
                  </Link>
                </Typography>
              ))}
            </Stack>
          </Stack>
        )}

        <Divider sx={{ margin: "16px 0" }} />

        <Stack spacing={2}>
          <Typography variant="h5">Picture Verification</Typography>
          <Stack direction="row" spacing={3}>
            <Stack spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary">
                Profile Photo
              </Typography>
              <PhotoProvider>
                <PhotoView src={ImgSrc(user.profileImage?.path ?? "")}>
                  <Avatar
                    alt="Profile photo"
                    variant="rounded"
                    src={ImgSrc(user.profileImage?.path ?? "")}
                    sx={{ height: 172, width: 172, cursor: "pointer" }}
                  />
                </PhotoView>
              </PhotoProvider>
            </Stack>
            {user.verificationSelfie?.path && (
              <Stack spacing={1} alignItems="center">
                <Typography variant="caption" color="text.secondary">
                  Verification Selfie
                </Typography>
                <PhotoProvider>
                  <PhotoView
                    src={ImgSrc(user.verificationSelfie.path)}
                  >
                    <Avatar
                      alt="Verification selfie"
                      variant="rounded"
                      src={ImgSrc(user.verificationSelfie.path)}
                      sx={{ height: 172, width: 172, cursor: "pointer" }}
                    />
                  </PhotoView>
                </PhotoProvider>
              </Stack>
            )}
          </Stack>
        </Stack>

        {!!user.photos?.length && (
          <Stack spacing={2} mt={3}>
            <Typography variant="h5">Additional Photos</Typography>
            <PhotoProvider>
              <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
                {user.photos.map((photo, index) => (
                  <Stack
                    key={photo.path ?? index}
                    spacing={1}
                    alignItems="center"
                  >
                    <Typography variant="caption" color="text.secondary">
                      Extra photo {index + 1}
                    </Typography>
                    <PhotoView src={ImgSrc(photo.path ?? "")}>
                      <Avatar
                        alt={`Extra photo ${index + 1}`}
                        variant="rounded"
                        src={ImgSrc(photo.path ?? "")}
                        sx={{ height: 172, width: 172, cursor: "pointer" }}
                      />
                    </PhotoView>
                  </Stack>
                ))}
              </Stack>
            </PhotoProvider>
          </Stack>
        )}

        {user.vettingNote && (
          <Stack mt={4} spacing={1}>
            <Typography variant="body2" color="gray">
              {user.status === UserStatus.REJECTED && "Rejected Reason"}
              {user.status === UserStatus.NOT_FINISHED_PROFILE &&
                "Vetting Note"}
            </Typography>
            <Box
              mt={1}
              p={2}
              borderRadius={2}
              sx={{ border: "1px solid #333" }}
            >
              <Typography variant="body2">{user.vettingNote}</Typography>
            </Box>
          </Stack>
        )}
      </Grid>
    </Stack>
  );
};

export { VettingShow };
